import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { BellRing, GlassWater, HandPlatter, Receipt, Utensils } from 'lucide-react';
import { ServiceRequestType } from '@/lib/shared-types';
import { raiseServiceRequest } from '@/lib/mutations';
import { errorMessage } from '@/lib/guest';
import { Card, CardContent } from '@/components/ui/card';

const REQUESTS = [
  { type: ServiceRequestType.CALL_WAITER, key: 'service.callWaiter', icon: BellRing },
  { type: ServiceRequestType.REQUEST_BILL, key: 'service.requestBill', icon: Receipt },
  { type: ServiceRequestType.WATER, key: 'service.water', icon: GlassWater },
  { type: ServiceRequestType.NAPKINS, key: 'service.napkins', icon: Utensils },
  { type: ServiceRequestType.PLATES, key: 'service.plates', icon: HandPlatter },
];

/** Requests route to the waiter assigned to this area and escalate on SLA. */
export default function ServiceRequests() {
  const { t } = useTranslation();
  const [pending, setPending] = useState<string | null>(null);

  /**
   * Queued before the network is touched. A guest who taps "call the
   * waiter" as they walk out of range still gets their waiter: the request is
   * on disk, and they are told it is waiting rather than that it arrived.
   */
  const send = async (type: string) => {
    setPending(type);
    try {
      // `sessionId` is bound from the guest token, so it is not sent.
      const { synced } = await raiseServiceRequest(type);
      toast.success(synced ? t('service.requestSent') : t('guest.sync.queued'));
    } catch (error) {
      toast.error(errorMessage(error, t('guest.service.failed')));
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="grid grid-cols-2 gap-3">
      {REQUESTS.map(({ type, key, icon: Icon }) => (
        <Card key={type}>
          <CardContent className="p-0">
            <button
              type="button"
              disabled={pending === type}
              onClick={() => void send(type)}
              className="flex h-32 w-full flex-col items-center justify-center gap-2 disabled:opacity-50"
            >
              <Icon className="h-8 w-8 text-accent" />
              <span className="px-2 text-center text-sm font-medium">{t(key)}</span>
            </button>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
